import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import './LoadingBar.css';

export interface LoadingBarProps {
  isLoading: boolean;
  message?: string;
  className?: string;
  delay?: number; // Minimum delay before showing (default 250ms)
}

interface LoadingState {
  isVisible: boolean;
  message: string;
}

export const LoadingBar: React.FC<LoadingBarProps> = ({
  isLoading,
  message = 'Loading...',
  className = '',
  delay = 250
}) => {
  const [loadingState, setLoadingState] = useState<LoadingState>({
    isVisible: false,
    message: message
  });
  const showTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Handle loading state with delay to prevent flashing
  useEffect(() => {
    if (isLoading) {
      // Clear any existing hide timer
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }

      // Set timer to show loading bar after delay
      showTimerRef.current = setTimeout(() => {
        setLoadingState(prev => ({
          isVisible: true,
          message: message
        }));
        showTimerRef.current = null;
      }, delay);
    } else {
      // Clear any existing show timer
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }

      // Hide immediately if already visible, or add small delay if not
      if (loadingState.isVisible) {
        hideTimerRef.current = setTimeout(() => {
          setLoadingState(prev => ({
            ...prev,
            isVisible: false
          }));
          hideTimerRef.current = null;
        }, 150);
      } else {
        setLoadingState(prev => ({
          ...prev,
          isVisible: false
        }));
      }
    }

    // Cleanup timers on unmount or dependency change
    return () => {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [isLoading, message, delay, loadingState.isVisible]);

  // Don't render if not visible
  if (!loadingState.isVisible) {
    return null;
  }

  // Use React Portal to render at document root level
  return createPortal(
    <div className={`loading-bar ${className}`}>
      <div className="loading-bar-overlay" />
      <div className="loading-bar-content">
        <div className="loading-spinner">
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
        </div>
        <div className="loading-message">{loadingState.message}</div>
      </div>
    </div>,
    document.body
  );
};
